import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type {
  LocationFormData,
  ShipmentFormData,
  VehiclesFormData,
} from '#/features/order-create'
import type { ShipmentDetail } from '#/shared/api/generated/shipment/models'
import {
  locationToFormDefaults,
  shipmentToFormDefaults,
  tagsToSeedOptions,
  vehiclesToFormDefaults,
} from '../lib/to-form-defaults'
import { NotesSection } from './notes-section'
import type { NotesValue } from './notes-section'
import { RouteSection } from './route-section'
import { VehicleSection } from './vehicle-section'

interface ShipmentCardProps {
  shipment?: ShipmentDetail
  onEditAll?: () => void
  onVehiclesSave?: (data: VehiclesFormData) => void | Promise<void>
  onRouteSave?: (data: {
    location: LocationFormData
    shipment: ShipmentFormData
  }) => void | Promise<void>
  onNotesSave?: (notes: {
    lbNote: string
    cmNote: string
  }) => void | Promise<void>
  compactRoute?: boolean
  customerNoteOnly?: boolean
  readOnly?: boolean
}

function CardHeader({ onEditAll }: { onEditAll?: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1.25">
      <div className="flex h-8 items-center justify-between px-5 pb-1.25">
        <h3 className="text-base font-semibold text-gray-900">
          {t('shipmentCard.title')}
        </h3>
        <div className="flex items-center gap-2.5">
          {onEditAll && (
            <button
              type="button"
              onClick={onEditAll}
              className="flex cursor-pointer items-center rounded-md px-2 py-0.75 text-sm font-medium text-primary hover:bg-blue-50"
            >
              {t('shipmentCard.editAll')}
            </button>
          )}
        </div>
      </div>
      <div className="h-px w-full bg-gray-200" />
    </div>
  )
}

interface ShipmentCardData {
  vehicles: NonNullable<ShipmentDetail['vehicles']>
  stops: NonNullable<ShipmentDetail['stops']>
  trailer: ShipmentDetail['transport_type'] | undefined
  estimatedPickupAt: ShipmentDetail['estimated_pickup_at']
  estimatedDeliveryAt: ShipmentDetail['estimated_delivery_at']
  firstAvailableDate: ShipmentDetail['first_available_date']
  lbNote: string
  cmNote: string
}

const EMPTY_SHIPMENT_CARD_DATA: ShipmentCardData = {
  vehicles: [],
  stops: [],
  trailer: undefined,
  estimatedPickupAt: undefined,
  estimatedDeliveryAt: undefined,
  firstAvailableDate: undefined,
  lbNote: '',
  cmNote: '',
}

function pickShipmentCardData(
  shipment: ShipmentDetail | undefined,
): ShipmentCardData {
  if (!shipment) return EMPTY_SHIPMENT_CARD_DATA
  return {
    vehicles: shipment.vehicles ?? [],
    stops: shipment.stops ?? [],
    trailer: shipment.transport_type,
    estimatedPickupAt: shipment.estimated_pickup_at,
    estimatedDeliveryAt: shipment.estimated_delivery_at,
    firstAvailableDate: shipment.first_available_date,
    lbNote: shipment.loadboard_instructions ?? '',
    cmNote: shipment.instructions ?? '',
  }
}

interface SectionEditor<T> {
  editing: boolean
  start: () => void
  cancel: () => void
  save: (data: T) => Promise<void>
}

function useSectionEditor<T>(
  onSave?: (data: T) => void | Promise<void>,
): SectionEditor<T> {
  const [editing, setEditing] = useState(false)
  const save = async (data: T) => {
    try {
      await onSave?.(data)
      setEditing(false)
    } catch {
      // keep the editor open so the user can correct and retry
    }
  }
  return {
    editing,
    start: () => setEditing(true),
    cancel: () => setEditing(false),
    save,
  }
}

function useShipmentCardDefaults(shipment: ShipmentDetail | undefined) {
  const vehiclesDefaults = useMemo(
    () => vehiclesToFormDefaults(shipment),
    [shipment],
  )
  const locationDefaults = useMemo(
    () => locationToFormDefaults(shipment),
    [shipment],
  )
  const shipmentDefaults = useMemo(
    () => shipmentToFormDefaults(shipment),
    [shipment],
  )
  const tagSeedOptions = useMemo(() => tagsToSeedOptions(shipment), [shipment])
  return {
    vehiclesDefaults,
    locationDefaults,
    shipmentDefaults,
    tagSeedOptions,
  }
}

export function ShipmentCard({
  shipment,
  onEditAll,
  onVehiclesSave,
  onRouteSave,
  onNotesSave,
  compactRoute,
  customerNoteOnly,
  readOnly,
}: ShipmentCardProps) {
  const data = pickShipmentCardData(shipment)
  const defaults = useShipmentCardDefaults(shipment)
  const vehicle = useSectionEditor<VehiclesFormData>(onVehiclesSave)
  const route = useSectionEditor<{
    location: LocationFormData
    shipment: ShipmentFormData
  }>(onRouteSave)
  const notes = useSectionEditor<NotesValue>(onNotesSave)

  return (
    <div className="flex w-full flex-col gap-3.75 rounded-md border border-gray-200 bg-white pt-2.5 pb-5">
      <CardHeader onEditAll={onEditAll} />

      <VehicleSection
        vehicles={data.vehicles}
        editing={vehicle.editing}
        onEditStart={vehicle.start}
        onCancel={vehicle.cancel}
        onSave={vehicle.save}
        defaultValues={defaults.vehiclesDefaults}
        readOnly={readOnly}
      />

      <RouteSection
        stops={data.stops}
        trailer={data.trailer}
        estimatedPickupAt={data.estimatedPickupAt}
        estimatedDeliveryAt={data.estimatedDeliveryAt}
        firstAvailableDate={data.firstAvailableDate}
        editing={route.editing}
        onEditStart={route.start}
        onCancel={route.cancel}
        onSave={route.save}
        locationDefaults={defaults.locationDefaults}
        shipmentDefaults={defaults.shipmentDefaults}
        tagSeedOptions={defaults.tagSeedOptions}
        compact={compactRoute}
        customer={shipment?.customer}
        readOnly={readOnly}
      />

      <NotesSection
        lbNote={data.lbNote}
        cmNote={data.cmNote}
        editing={notes.editing}
        onEditStart={notes.start}
        onCancel={notes.cancel}
        onSave={notes.save}
        customerNoteOnly={customerNoteOnly}
        readOnly={readOnly}
      />
    </div>
  )
}
